import { useState } from "react";
import { Download, Upload, FileSpreadsheet, FileText, Users, BarChart3, BookOpen, Calculator, Brain, Loader2, ChevronDown, ChevronUp, Heart, Zap, Sparkles, ClipboardList, Activity, Database, Layers, FlaskConical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { apiCall } from "../App";

const ReportCard = ({ title, description, icon: Icon, actions, testId }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="p-4 rounded-xl bg-cosmic-light/10 border border-divine-gold/10 hover:border-divine-gold/30 transition-all" data-testid={testId}>
      <div className="flex items-start justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-start gap-3 flex-1">
          <div className="p-2 rounded-lg bg-divine-gold/10"><Icon className="w-5 h-5 text-divine-gold" /></div>
          <div className="flex-1">
            <h4 className="text-divine-gold font-medium text-sm">{title}</h4>
            <p className="text-chakra-crown/50 text-xs mt-0.5">{description}</p>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-chakra-crown/40" /> : <ChevronDown className="w-4 h-4 text-chakra-crown/40" />}
      </div>
      {expanded && <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-divine-gold/10">{actions}</div>}
    </div>
  );
};

const DownloadBtn = ({ label, onClick, loading, icon: Icon = Download, testId }) => (
  <Button size="sm" onClick={onClick} disabled={loading} className="bg-cosmic-deep border border-divine-gold/20 text-chakra-crown hover:bg-divine-gold/10 hover:border-divine-gold/40 text-xs" data-testid={testId}>
    {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Icon className="w-3 h-3 mr-1" />}
    {label}
  </Button>
);

const ImportSection = ({ endpoint, label, token, onSuccess, customEndpoint }) => {
  const [mode, setMode] = useState("merge");
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const url = customEndpoint ? `${API_URL}${customEndpoint}?mode=${mode}` : `${API_URL}/api/reports/${endpoint}?mode=${mode}`;
      const res = await fetch(url, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: formData,
      });
      const data = await res.json();
      if (res.ok) { toast.success(data.message || "Import successful"); onSuccess?.(); }
      else { toast.error(data.detail || "Import failed"); }
    } catch (err) { toast.error("Import failed: " + err.message); }
    setUploading(false);
    e.target.value = "";
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="flex rounded-full overflow-hidden border border-divine-gold/20">
        <button onClick={() => setMode("merge")} className={`px-2.5 py-0.5 text-xs ${mode === "merge" ? "bg-divine-gold text-cosmic-void" : "bg-cosmic-deep text-chakra-crown/50"}`} data-testid={`import-mode-merge-${endpoint}`}>Merge</button>
        <button onClick={() => setMode("overwrite")} className={`px-2.5 py-0.5 text-xs ${mode === "overwrite" ? "bg-chakra-root text-white" : "bg-cosmic-deep text-chakra-crown/50"}`} data-testid={`import-mode-overwrite-${endpoint}`}>Overwrite</button>
      </div>
      <label className="cursor-pointer" data-testid={`import-btn-${endpoint}`}>
        <input type="file" accept=".xlsx,.xls" onChange={handleUpload} className="hidden" />
        <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-cosmic-deep border border-divine-gold/20 text-chakra-crown hover:bg-divine-gold/10 hover:border-divine-gold/40 text-xs transition-all">
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
          {uploading ? "Importing..." : `Import ${label}`}
        </span>
      </label>
    </div>
  );
};

const downloadFile = async (url, token, filename) => {
  try {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const fullUrl = url.startsWith("http") ? url : url.startsWith("/api/") ? `${API_URL}${url}` : `${API_URL}/api/reports/${url}`;
    const res = await fetch(fullUrl, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) {
      let errMsg = "Download failed";
      try { const err = await res.json(); errMsg = err.detail || errMsg; } catch {}
      toast.error(errMsg); return;
    }
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`Downloaded ${filename}`);
  } catch (err) { toast.error("Download failed: " + err.message); }
};

export const AdminReportsPanel = ({ token }) => {
  const [loading, setLoading] = useState({});

  const dl = async (key, url, filename) => {
    setLoading(p => ({ ...p, [key]: true }));
    await downloadFile(url, token, filename);
    setLoading(p => ({ ...p, [key]: false }));
  };

  return (
    <div className="space-y-6" data-testid="admin-reports-panel">
      {/* Section 1: Base Model Data */}
      <div>
        <h3 className="font-heading text-lg text-divine-gold mb-3 flex items-center gap-2">
          <Calculator className="w-5 h-5" /> BASE MODEL DATA
          <Badge variant="outline" className="text-xs border-divine-gold/20 text-divine-gold/60">Export & Import</Badge>
        </h3>
        <div className="space-y-3">
          <ReportCard title="Perception Pair Table" description="All 175 triggers with false/real perceptions, abhideya, guna flow, gita verses"
            icon={Brain} testId="report-perception-pairs"
            actions={<>
              <DownloadBtn label="Excel" onClick={() => dl("pp_xl", "export/perception_pairs?format=excel", "perception_pairs.xlsx")} loading={loading.pp_xl} icon={FileSpreadsheet} testId="dl-perception-excel" />
              <DownloadBtn label="PDF" onClick={() => dl("pp_pdf", "export/perception_pairs?format=pdf", "perception_pairs.pdf")} loading={loading.pp_pdf} icon={FileText} testId="dl-perception-pdf" />
              <ImportSection endpoint="import/perception_pairs" label="Excel" token={token} />
            </>} />

          <ReportCard title="12D Profile Engine" description="Question weights, pillar maps, NLP keywords, abhideya practices"
            icon={BarChart3} testId="report-profile-engine"
            actions={<>
              <DownloadBtn label="Excel" onClick={() => dl("pe_xl", "export/profile_engine", "12d_profile_engine.xlsx")} loading={loading.pe_xl} icon={FileSpreadsheet} testId="dl-engine-excel" />
              <ImportSection endpoint="import/profile_engine" label="NLP Keywords" token={token} />
            </>} />

          <ReportCard title="Gita / Scriptural Verse Database" description="All unique Gita verses with linked pillars, abhideyas, and triggers"
            icon={BookOpen} testId="report-gita-verses"
            actions={<>
              <DownloadBtn label="Excel" onClick={() => dl("gv_xl", "export/gita_verses", "gita_verse_database.xlsx")} loading={loading.gv_xl} icon={FileSpreadsheet} testId="dl-gita-excel" />
              <ImportSection endpoint="import/gita_verses" label="Gita Verses" token={token} />
            </>} />

          <ReportCard title="3A Framework Scripture Database" description="Vikara scriptures, Alignment steps, Awakening wisdoms, Guna/Kosha insights, Custom verses — knowledge base for auto-generated reports"
            icon={BookOpen} testId="report-3a-scripture-db"
            actions={<>
              <DownloadBtn label="Export Excel" onClick={() => dl("3a_sdb", "/api/reports-3a/scripture-db/export", "3a_scripture_database.xlsx")} loading={loading["3a_sdb"]} icon={FileSpreadsheet} testId="dl-3a-scripture-db" />
              <ImportSection endpoint="" label="Scripture DB" token={token} customEndpoint="/api/reports-3a/scripture-db/import" />
            </>} />

          <ReportCard title="Formula Matrices" description="Trinity Gap, Pillar Guna Matrix, Energy Polarity, Guna Skewness, Vikara formulas"
            icon={Calculator} testId="report-formulas"
            actions={<>
              <DownloadBtn label="Excel" onClick={() => dl("fm_xl", "export/formula_matrices", "formula_matrices.xlsx")} loading={loading.fm_xl} icon={FileSpreadsheet} testId="dl-formulas-excel" />
              <ImportSection endpoint="import/formula_matrices" label="Formulas" token={token} />
            </>} />
        </div>
      </div>

      {/* Section 2: Diagnostic Reports */}
      <div>
        <h3 className="font-heading text-lg text-divine-gold mb-3 flex items-center gap-2">
          <Activity className="w-5 h-5" /> DIAGNOSTIC REPORTS
          <Badge variant="outline" className="text-xs border-emerald-500/30 text-emerald-400">New</Badge>
        </h3>
        <div className="space-y-3">
          <ReportCard title="Life Diagnostic (9-Module)" description="All clients' 9-module life diagnostic scores and answers (Physical, Mental, Emotional, Social, Spiritual, Financial, Career, Intellectual, Environmental)"
            icon={ClipboardList} testId="report-life-diagnostic"
            actions={<>
              <DownloadBtn label="Excel" onClick={() => dl("ld_xl", "life-diagnostic/export", "life_diagnostic_report.xlsx")} loading={loading.ld_xl} icon={FileSpreadsheet} testId="dl-life-diag-excel" />
            </>} />

          <ReportCard title="Deep Dive Kosha Quiz" description="All clients' Kosha quiz results for both Relationship and Life modules (Annamaya, Pranamaya, Manomaya, Vijnanamaya, Anandamaya)"
            icon={Heart} testId="report-kosha-quiz"
            actions={<>
              <DownloadBtn label="Excel" onClick={() => dl("kq_xl", "kosha-quiz/export", "kosha_quiz_report.xlsx")} loading={loading.kq_xl} icon={FileSpreadsheet} testId="dl-kosha-quiz-excel" />
            </>} />

          <ReportCard title="Tatkalik (Contemporary Habits)" description="All clients' contemporary habit trigger selections for both Relationship and Life modules"
            icon={Zap} testId="report-tatkalik"
            actions={<>
              <DownloadBtn label="Excel" onClick={() => dl("tk_xl", "tatkalik/export", "tatkalik_contemporary_habits.xlsx")} loading={loading.tk_xl} icon={FileSpreadsheet} testId="dl-tatkalik-excel" />
            </>} />
        </div>
      </div>

      {/* Section 2A: Master 3A+Harmony Pipeline Templates */}
      <div>
        <h3 className="font-heading text-lg text-divine-gold mb-3 flex items-center gap-2">
          <Layers className="w-5 h-5" /> MASTER 3A + HARMONY PIPELINE
          <Badge variant="outline" className="text-xs border-rose-500/30 text-rose-400">Full Pipeline</Badge>
        </h3>
        <p className="text-chakra-crown/50 text-xs mb-3">Unified templates per module with ALL assessment types and their complete Awareness → Alignment → Awakening → Harmony data flow. Export, fill empty columns, and re-import.</p>
        <div className="space-y-3">
          <ReportCard title="Relationship Wisdom Coaching — Full 3A Pipeline" description="6 sheets: Issue-Specific (10 pts), Tatkalik (25 triggers), Vikara (6), Guna (3), Kosha (5 layers), Energy Polarity (3 types) — each with full awareness→alignment→awakening→harmony columns"
            icon={Heart} testId="report-master-relationship"
            actions={<>
              <DownloadBtn label="Export Master" onClick={() => dl("mr_xl", "master/relationship/export", "relationship_master_3a_pipeline.xlsx")} loading={loading.mr_xl} icon={FileSpreadsheet} testId="dl-master-relationship" />
              <ImportSection endpoint="master/relationship/import" label="Pipeline" token={token} />
            </>} />

          <ReportCard title="Life Wisdom Coaching — Full 3A Pipeline" description="7 sheets: Issue-Specific (20 pts), Tatkalik (25), Vikara (6), Guna (3), Kosha (5), Energy Polarity (3), Life Diagnostic (9 modules) — each with full pipeline columns"
            icon={Brain} testId="report-master-life"
            actions={<>
              <DownloadBtn label="Export Master" onClick={() => dl("ml_xl", "master/life/export", "life_master_3a_pipeline.xlsx")} loading={loading.ml_xl} icon={FileSpreadsheet} testId="dl-master-life" />
              <ImportSection endpoint="master/life/import" label="Pipeline" token={token} />
            </>} />
        </div>
      </div>

      {/* Section 2B: Assessment Universe Data (Import/Export) */}
      <div>
        <h3 className="font-heading text-lg text-divine-gold mb-3 flex items-center gap-2">
          <Database className="w-5 h-5" /> ASSESSMENT UNIVERSE DATA
          <Badge variant="outline" className="text-xs border-sky-500/30 text-sky-400">Import & Export</Badge>
        </h3>
        <p className="text-chakra-crown/50 text-xs mb-3">Master templates for all diagnostic tests and assessments. Export to review, edit in Excel, and re-import to update the system.</p>
        <div className="space-y-3">
          <ReportCard title="Issue-Specific Diagnosis (Relationship + Life)" description="Pain point templates with full 3A+Harmony mapping: awareness, agreement, boundary, repair_action, reflection, guna, vikara, harmony"
            icon={ClipboardList} testId="report-universe-issue-diagnosis"
            actions={<>
              <DownloadBtn label="Export Excel" onClick={() => dl("uid_xl", "universe/issue-diagnosis/export", "issue_specific_universe.xlsx")} loading={loading.uid_xl} icon={FileSpreadsheet} testId="dl-universe-issue-diag" />
              <ImportSection endpoint="universe/issue-diagnosis/import" label="Pain Points" token={token} />
            </>} />

          <ReportCard title="Tatkalik Triggers Universe" description="25 contemporary habit triggers with perception shifts + alignment, awakening, harmony columns"
            icon={Zap} testId="report-universe-tatkalik"
            actions={<>
              <DownloadBtn label="Export Excel" onClick={() => dl("utk_xl", "universe/tatkalik/export", "tatkalik_universe.xlsx")} loading={loading.utk_xl} icon={FileSpreadsheet} testId="dl-universe-tatkalik" />
              <ImportSection endpoint="universe/tatkalik/import" label="Triggers" token={token} />
            </>} />

          <ReportCard title="Vikara Self-Assessment Questions" description="6 Vikara questions (Kama, Krodha, Lobha, Moha, Matsarya, Mada) + rating options"
            icon={FlaskConical} testId="report-universe-vikara"
            actions={<>
              <DownloadBtn label="Export Excel" onClick={() => dl("uvk_xl", "universe/vikara/export", "vikara_universe.xlsx")} loading={loading.uvk_xl} icon={FileSpreadsheet} testId="dl-universe-vikara" />
              <ImportSection endpoint="universe/vikara/import" label="Vikara Qs" token={token} />
            </>} />

          <ReportCard title="Guna Self-Assessment Questions" description="3 Guna questions (Sattva, Rajas, Tamas) + rating options"
            icon={FlaskConical} testId="report-universe-guna"
            actions={<>
              <DownloadBtn label="Export Excel" onClick={() => dl("ugn_xl", "universe/guna/export", "guna_universe.xlsx")} loading={loading.ugn_xl} icon={FileSpreadsheet} testId="dl-universe-guna" />
              <ImportSection endpoint="universe/guna/import" label="Guna Qs" token={token} />
            </>} />

          <ReportCard title="Kosha Quiz Questions (Relationship + Life)" description="50 Kosha questions (25 per module, 5 per Kosha layer) with full option sets"
            icon={Layers} testId="report-universe-kosha"
            actions={<>
              <DownloadBtn label="Export Excel" onClick={() => dl("ukq_xl", "universe/kosha-quiz/export", "kosha_quiz_universe.xlsx")} loading={loading.ukq_xl} icon={FileSpreadsheet} testId="dl-universe-kosha" />
              <ImportSection endpoint="universe/kosha-quiz/import" label="Kosha Qs" token={token} />
            </>} />

          <ReportCard title="Life Diagnostic Modules & Questions" description="9 diagnostic modules + 62 questions with types, options, and scoring config"
            icon={Brain} testId="report-universe-life-diagnostic"
            actions={<>
              <DownloadBtn label="Export Excel" onClick={() => dl("uld_xl", "universe/life-diagnostic/export", "life_diagnostic_universe.xlsx")} loading={loading.uld_xl} icon={FileSpreadsheet} testId="dl-universe-life-diag" />
              <ImportSection endpoint="universe/life-diagnostic/import" label="Life Modules" token={token} />
            </>} />
        </div>
      </div>

      {/* Section 3: Client Reports */}
      <div>
        <h3 className="font-heading text-lg text-divine-gold mb-3 flex items-center gap-2">
          <Users className="w-5 h-5" /> CLIENT REPORTS
        </h3>
        <div className="space-y-3">
          <ReportCard title="All Client Profiles" description="Basic profiles of all registered clients with coach assignments"
            icon={Users} testId="report-client-profiles"
            actions={<>
              <DownloadBtn label="Excel" onClick={() => dl("cp_xl", "clients/profiles?format=excel", "all_client_profiles.xlsx")} loading={loading.cp_xl} icon={FileSpreadsheet} testId="dl-clients-excel" />
              <DownloadBtn label="PDF" onClick={() => dl("cp_pdf", "clients/profiles?format=pdf", "all_client_profiles.pdf")} loading={loading.cp_pdf} icon={FileText} testId="dl-clients-pdf" />
            </>} />

          <ReportCard title="All Clients Profiling Data (12D)" description="Q&A responses, scores, trigger boards for all profiled clients"
            icon={BarChart3} testId="report-all-profiling"
            actions={<>
              <DownloadBtn label="Excel" onClick={() => dl("ap_xl", "clients/profiling_all", "all_clients_profiling.xlsx")} loading={loading.ap_xl} icon={FileSpreadsheet} testId="dl-all-profiling-excel" />
            </>} />

          <ReportCard title="Client Compliance Summary" description="Comprehensive progress: 12D profiling, diagnostics, kosha quizzes, tatkalik, practice assignments, alignment/awakening/harmony days"
            icon={BarChart3} testId="report-compliance"
            actions={<>
              <DownloadBtn label="Excel" onClick={() => dl("cc_xl", "clients/compliance", "client_compliance_summary.xlsx")} loading={loading.cc_xl} icon={FileSpreadsheet} testId="dl-compliance-excel" />
            </>} />

          <ReportCard title="Coach Notes Export" description="All session notes across all clients"
            icon={FileText} testId="report-notes"
            actions={<>
              <DownloadBtn label="Excel" onClick={() => dl("ne_xl", "notes/export", "coach_notes_export.xlsx")} loading={loading.ne_xl} icon={FileSpreadsheet} testId="dl-notes-excel" />
            </>} />
        </div>
      </div>

      {/* Section 4: Practice Reports */}
      <div>
        <h3 className="font-heading text-lg text-divine-gold mb-3 flex items-center gap-2">
          <Sparkles className="w-5 h-5" /> PRACTICE REPORTS
          <Badge variant="outline" className="text-xs border-emerald-500/30 text-emerald-400">New</Badge>
        </h3>
        <div className="space-y-3">
          <ReportCard title="Practice Universe" description="Full catalog of all 126 practices with categories, kosha targets, difficulty, duration"
            icon={BookOpen} testId="report-practice-universe"
            actions={<>
              <DownloadBtn label="Excel" onClick={() => dl("pu_xl", "../practices/export", "practice_universe.xlsx")} loading={loading.pu_xl} icon={FileSpreadsheet} testId="dl-practice-universe-excel" />
            </>} />

          <ReportCard title="Practice Assignments" description="All client practice assignments with category, kosha, difficulty, and daily completion log"
            icon={ClipboardList} testId="report-practice-assignments"
            actions={<>
              <DownloadBtn label="Excel" onClick={() => dl("pa_xl", "practice-assignments/export", "practice_assignments_report.xlsx")} loading={loading.pa_xl} icon={FileSpreadsheet} testId="dl-practice-assign-excel" />
            </>} />
        </div>
      </div>

      {/* Section 5: Coach & Expert Reports */}
      <div>
        <h3 className="font-heading text-lg text-divine-gold mb-3 flex items-center gap-2">
          <Users className="w-5 h-5" /> COACH & EXPERT REPORTS
        </h3>
        <div className="space-y-3">
          <ReportCard title="Coach Profiles & Client Mapping" description="All coaches with specializations and assigned client trees"
            icon={Users} testId="report-coach-profiles"
            actions={<>
              <DownloadBtn label="Excel" onClick={() => dl("ch_xl", "coaches/profiles?format=excel", "coach_profiles_mapping.xlsx")} loading={loading.ch_xl} icon={FileSpreadsheet} testId="dl-coaches-excel" />
              <DownloadBtn label="PDF" onClick={() => dl("ch_pdf", "coaches/profiles?format=pdf", "coach_profiles_mapping.pdf")} loading={loading.ch_pdf} icon={FileText} testId="dl-coaches-pdf" />
            </>} />

          <ReportCard title="Expert Profiles & Categories" description="All experts with specializations, assigned practice categories, and client mappings"
            icon={Sparkles} testId="report-expert-profiles"
            actions={<>
              <DownloadBtn label="Excel" onClick={() => dl("ex_xl", "experts/profiles", "expert_profiles_mapping.xlsx")} loading={loading.ex_xl} icon={FileSpreadsheet} testId="dl-experts-excel" />
            </>} />
        </div>
      </div>
    </div>
  );
};

export const CoachReportsPanel = ({ token }) => {
  const [loading, setLoading] = useState({});

  const dl = async (key, url, filename) => {
    setLoading(p => ({ ...p, [key]: true }));
    await downloadFile(url, token, filename);
    setLoading(p => ({ ...p, [key]: false }));
  };

  return (
    <div className="space-y-6" data-testid="coach-reports-panel">
      <div>
        <h3 className="font-heading text-lg text-divine-gold mb-3 flex items-center gap-2">
          <Users className="w-5 h-5" /> MY CLIENT REPORTS
        </h3>
        <div className="space-y-3">
          <ReportCard title="Assigned Clients Profiling" description="Q&A data and profiling reports for your assigned clients"
            icon={BarChart3} testId="report-coach-profiling"
            actions={<>
              <DownloadBtn label="Excel" onClick={() => dl("mc_xl", "coach/my_clients/profiling?format=excel", "my_clients_profiling.xlsx")} loading={loading.mc_xl} icon={FileSpreadsheet} testId="dl-coach-profiling-excel" />
              <DownloadBtn label="PDF" onClick={() => dl("mc_pdf", "coach/my_clients/profiling?format=pdf", "my_clients_profiling.pdf")} loading={loading.mc_pdf} icon={FileText} testId="dl-coach-profiling-pdf" />
            </>} />

          <ReportCard title="My Client Notes" description="Export all session notes for your assigned clients"
            icon={FileText} testId="report-coach-notes"
            actions={<>
              <DownloadBtn label="Excel" onClick={() => dl("cn_xl", "notes/export", "my_client_notes.xlsx")} loading={loading.cn_xl} icon={FileSpreadsheet} testId="dl-coach-notes-excel" />
            </>} />
        </div>
      </div>
    </div>
  );
};

export const ClientProfilingDownload = ({ userId, clientName, token }) => {
  const [loading, setLoading] = useState({});

  const dl = async (key, format) => {
    setLoading(p => ({ ...p, [key]: true }));
    const ext = format === "pdf" ? "pdf" : "xlsx";
    await downloadFile(`client/${userId}/profiling?format=${format}`, token, `profiling_${clientName.replace(/\s+/g, '_')}.${ext}`);
    setLoading(p => ({ ...p, [key]: false }));
  };

  return (
    <div className="flex gap-2" data-testid="client-profiling-download">
      <DownloadBtn label="Excel" onClick={() => dl("xl", "excel")} loading={loading.xl} icon={FileSpreadsheet} testId="dl-client-profiling-excel" />
      <DownloadBtn label="PDF" onClick={() => dl("pdf", "pdf")} loading={loading.pdf} icon={FileText} testId="dl-client-profiling-pdf" />
    </div>
  );
};
